import { Box, Button, Center, HStack, Image, Spinner, Stack, Text } from "@chakra-ui/react";
import { useAppSelector } from "hooks/store/hooks";
import React, { useEffect, useState } from "react";
import { selectCurrentDevice } from "stores/auth/authSlice";
import { selectColorThemeMode } from "stores/ui/uiSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "TranslationContext";
import fallbackbanner from "components/Icons/fallbackbanner.png";
import { ASSETS, PATH } from "configs/brandConfig";
import { buildErrorQueryString, getLastError } from "utils/errorCapture";
import { resolveOriginalPosition } from "utils/sourceMapResolver";

// 3 crashes within CRASH_LOOP_WINDOW_MS → stop auto-reloading and show the
// manual FallbackUI buttons (reload / forcerefresh). Without this, a user
// with a recurring JS error gets stuck in an invisible reload loop and never
// sees the page (this is what produced crcnt=638 in CN logs).
// Counter resets if the previous crash was outside the window — a fresh
// crash long after a stable session is not a loop.
const MAX_CRASHES_IN_WINDOW = 3;
const CRASH_LOOP_WINDOW_MS = 10000;

// Pure compute of the loop-guard state — read storage, decide what the new
// counter would be, but DON'T write. Writing happens once in useEffect.
// React can invoke a lazy useState initializer more than once (StrictMode,
// concurrent rendering, transient remounts); side-effecting here would
// double-increment the counter and push the user into manual mode early.
const computeCrashState = () => {
  const lastCrashTime = parseInt(sessionStorage.getItem('lastCrashTime') || '0', 10);
  const currentTime = Date.now();
  const prevCount = parseInt(sessionStorage.getItem('crashCounter') || '0', 10);
  const withinWindow = lastCrashTime > 0 && (currentTime - lastCrashTime) < CRASH_LOOP_WINDOW_MS;
  const newCounter = withinWindow ? prevCount + 1 : 1;
  return {
    newCounter,
    currentTime,
    isManual: newCounter >= MAX_CRASHES_IN_WINDOW,
  };
};

const clearBrowserCaches = () => {
  if ("caches" in window) {
    caches.keys().then((names) => {
      names.forEach((name) => caches.delete(name));
    }).catch(() => {});
  }
  if (navigator.serviceWorker) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => registration.unregister());
    }).catch(() => {});
  }
};

const FallbackUI = () => {
  // Get the current color theme mode from the Redux store
  const colorThemeMode = useAppSelector(selectColorThemeMode);
  const { translate } = useTranslation();
  const currentDevice = useAppSelector(selectCurrentDevice); // Get the current device details
  const navigate = useNavigate();
  const [lastError] = useState(() => getLastError());
  // Counter / window logic runs once at mount so render can pick the right UI.
  const [crashState] = useState(computeCrashState);

  // Side effects must run after render so React doesn't re-fire them and so we
  // don't navigate during the boundary's own render pass.
  useEffect(() => {
    // Commit the new counter to sessionStorage exactly once per mount. Doing
    // this in the effect (not in the lazy init) keeps the increment idempotent
    // even if React invokes the initializer twice in dev StrictMode.
    try {
      sessionStorage.setItem('lastCrashTime', String(crashState.currentTime));
      sessionStorage.setItem('crashCounter', String(crashState.newCounter));
    } catch {
      // sessionStorage can throw in private mode / restricted webviews.
    }

    // Manual mode → no auto-redirect; user clicks reload / forcerefresh.
    if (crashState.isManual) return;

    clearBrowserCaches();

    // Try to map the minified chunk position back to the original .jsx line.
    // Capped at ~2s by the resolver — if the .map fetch is slow/blocked we
    // redirect with the chunk position rather than hanging the crashed UI.
    // Use the lastError captured at mount time (not re-read from storage)
    // so a later async capture can't overwrite it before we read.
    let cancelled = false;
    (async () => {
      const original = await resolveOriginalPosition(lastError);
      if (cancelled) return;
      // source-map-js can return {source, line: null, column: null} when it
      // resolves the file but not the precise position. Fall back to the
      // chunk values so we don't lose useful info on the redirect URL.
      const enriched = original
        ? {
            ...lastError,
            file: original.source,
            line: original.line != null ? original.line : lastError && lastError.line,
            col: original.column != null ? original.column : lastError && lastError.col,
          }
        : lastError;
      const errQuery = buildErrorQueryString(enriched);
      window.location.href = `${PATH.HOME}?crcnt=${crashState.newCounter}&time=${crashState.currentTime}${errQuery}`;
    })();
    return () => { cancelled = true; };
  }, [lastError, crashState]);

  const clearCacheAndReload = () => {
    sessionStorage.removeItem('crashCounter');
    sessionStorage.removeItem('lastCrashTime');
    if (currentDevice) {
      localStorage.removeItem(`showPopup_${currentDevice?.imei}`);
    }
    if (localStorage.getItem('consentMode') || localStorage.getItem('n8n-chat/sessionId')) {
      localStorage.removeItem('consentMode');
      localStorage.removeItem('n8n-chat/sessionId');
    }
    // Soft reload: keep the user's auth, device, language, and status settings
    // so they land back on their dashboard, not the login/device-select flow.
    // Only the "forcerefresh" button below does the nuclear localStorage.clear().

    clearBrowserCaches();

    // Single hard navigation. react-router's navigate() would prepend the
    // /UI basename and produce /UI/UI; an absolute href bypasses that.
    window.location.href = `/UI/?time=${Date.now()}`;
  };

  // Auto-recovery path (1st / 2nd crash within window): no scary error screen,
  // just a spinner while we redirect. The redirect itself runs in the effect.
  if (!crashState.isManual) {
    return (
      <Box
      width={"100%"}
      height={{base: "20vh", md: "70vh"}}
      borderRadius="4px"
      justifyContent="center"
      alignItems="center"
      display="flex"
    >
      <Box
        m="10px"
        bg={`${colorThemeMode}_background`}
        p="20px"
        borderRadius="4px"
      >
        <Center>
          <Spinner />
        </Center>
      </Box>
    </Box>
    );
  }

  // Manual mode (3rd consecutive crash): show full UI with recovery buttons.
  return (
<>
  <Center height="100%">
    <Stack gap={4}>
      <Box className="backToDashboard" display="flex">
        <Image src={ASSETS.LOGOS.fallback} />
      </Box>
      <Text textAlign="center">{translate["errormsg1"]}</Text>
      <Text textAlign="center">{translate["errormsg2"]}</Text>
      <HStack justify="center" spacing={4}>
  <Button
    height="36px"
    bgColor={`${colorThemeMode}_primary`}
    fontWeight={100}
    onClick={clearCacheAndReload}
  >
    {translate["reload"]}
  </Button>
  <Button
    height="36px"
    bgColor={`${colorThemeMode}_primary`}
    fontWeight={100}
    onClick={() => {
      sessionStorage.removeItem('crashCounter');
      sessionStorage.removeItem('lastCrashTime');
      localStorage.clear();
      navigate(`/UI?time=${new Date().getTime()}`);
      window.location.reload();
    }}
  >
   {translate['forcerefresh']}
  </Button>
</HStack>
    </Stack>
  </Center>
    </>
  );
};

export default FallbackUI;